import { Emoji } from '@nostr/gadgets/lists'
import { clsx, type ClassValue } from 'clsx'
import { parseNativeEmoji } from 'emoji-picker-react/src/dataUtils/parseNativeEmoji'
import { twMerge } from 'tailwind-merge'

export function now(): number {
  return Math.round(Date.now() / 1000)
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function isSafari() {
  if (typeof window === 'undefined' || !window.navigator) return false
  const ua = window.navigator.userAgent
  const vendor = window.navigator.vendor
  return /Safari/.test(ua) && /Apple Computer/.test(vendor) && !/Chrome/.test(ua)
}

export function isAndroid() {
  if (typeof window === 'undefined' || !window.navigator) return false
  const ua = window.navigator.userAgent
  return /android/i.test(ua)
}

export function isTorBrowser() {
  if (typeof window === 'undefined' || !window.navigator) return false
  const ua = window.navigator.userAgent
  return /torbrowser/i.test(ua)
}

export function isTouchDevice() {
  if (typeof window === 'undefined' || !window.navigator) return false
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

export function isInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

export function isPartiallyInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect()
  return (
    rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
    rect.bottom > 0 &&
    rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
    rect.right > 0
  )
}

export function isSupportCheckConnectionType() {
  if (typeof window === 'undefined' || !(navigator as any).connection) return false
  return typeof (navigator as any).connection.type === 'string'
}

export function isEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function isDevEnv() {
  return process.env.NODE_ENV === 'development'
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  maxWait?: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let maxTimeoutId: ReturnType<typeof setTimeout> | null = null

  return function (...args: Parameters<T>) {
    // clear the regular debounce timer
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }

    // start max wait timer on first call
    if (maxWait !== undefined && maxTimeoutId === null) {
      maxTimeoutId = setTimeout(() => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
        maxTimeoutId = null
        func(...args)
      }, maxWait)
    }

    // set the regular debounce timer
    timeoutId = setTimeout(() => {
      if (maxTimeoutId !== null) {
        clearTimeout(maxTimeoutId)
        maxTimeoutId = null
      }
      timeoutId = null
      func(...args)
    }, wait)
  }
}

export function batchDebounce<T>(func: (args: T[]) => void, delay: number, maxWait?: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let maxTimeoutId: ReturnType<typeof setTimeout> | null = null
  let accumulated: T[] = []

  const debouncedFunc = (arg: T) => {
    accumulated.push(arg)

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // Start max wait timer on first call
    if (maxWait !== undefined && maxTimeoutId === null) {
      maxTimeoutId = setTimeout(() => {
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
        maxTimeoutId = null
        func(accumulated)
        accumulated = []
      }, maxWait)
    }

    timeoutId = setTimeout(() => {
      if (maxTimeoutId) {
        clearTimeout(maxTimeoutId)
        maxTimeoutId = null
      }
      timeoutId = null
      func(accumulated)
      accumulated = []
    }, delay)
  }

  debouncedFunc.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    if (maxTimeoutId) {
      clearTimeout(maxTimeoutId)
      maxTimeoutId = null
    }
    accumulated = []
  }

  return debouncedFunc
}

export function parseEmojiPickerUnified(unified: string): string | Emoji | undefined {
  if (unified.startsWith(':')) {
    const secondColonIndex = unified.indexOf(':', 1)
    if (secondColonIndex < 0) return undefined

    const shortcode = unified.slice(1, secondColonIndex)
    const url = unified.slice(secondColonIndex + 1)
    return { shortcode, url }
  } else {
    return parseNativeEmoji(unified)
  }
}

export function parseYouTubeURL(text: string): { id: string; type: 'video' | 'playlist' } | null {
  if (typeof text !== 'string') return null

  // pull out the first plausible youtube URL in the string
  const urlMatch = text.match(/https?:\/\/[^\s]*(?:youtube\.com|youtu\.be)[^\s]*/i)
  if (!urlMatch) return null

  let url
  try {
    url = new URL(urlMatch[0])
  } catch {
    return null
  }

  const host = url.hostname.replace(/^(www\.|m\.|music\.)/, '')
  if (host !== 'youtube.com' && host !== 'youtu.be') return null

  let videoId = null

  if (host === 'youtu.be') {
    videoId = url.pathname.slice(1, 12) || null
  } else {
    const path = url.pathname
    if (path.startsWith('/watch')) {
      videoId = url.searchParams.get('v')
    } else if (
      path.startsWith('/shorts/') ||
      path.startsWith('/embed/') ||
      path.startsWith('/live/') ||
      path.startsWith('/v/')
    ) {
      videoId = path.split('/')[2] || null
    }
  }

  if (videoId) {
    return { id: videoId, type: 'video' }
  }

  const playlistId = url.searchParams.get('list')
  if (playlistId) {
    return { id: playlistId, type: 'playlist' }
  }

  return null
}
